import { Component } from 'react';
import { Redirect, Route, Switch } from 'react-router-dom';
import { RouteNames } from '../../shared/enums/RouteNames';
import ProtectedRoute from '../shared/ProtectedRoute/ProtectedRoute';
import { RouteRules, RouteRules as Rules } from '../../shared/RouteRules';

// Views
import Profile from './profile/Profile';
import Login from './login/Login';
import CreateAccountSuccess from './createAccountSuccess/CreateAccountSuccess';
import CreateAccount from './createAccount/CreateAccount';
import Preferences from './preferences/Preferences';
import ForgotPassword from './forgotPassword/ForgotPassword';
import TermsOfService from './termsOfService/TermsOfService';
import ManageHabitats from './manageHabitats/ManageHabitats';
import { connect } from 'react-redux';
import { ReduxStore } from '../../store/reducers';
import ForgotPasswordSuccess from './forgotPasswordSuccess/ForgotPasswordSuccess';
import HelpAndInfo from './helpAndInfo/HelpAndInfo';
import SavedObservations from './savedObservations/SavedObservations';
import ForgotPasswordCode from './forgotPasswordCode/ForgotPasswordCode';
import ManageUsers from './manageUsers/ManageUsers';
import EditObservations from './editObservations/EditObservations';
import CreateObservations from './createObservations/CreateObservations';
import AccountVerification from './accountVerification/AccountVerification';
import PrivacyPolicy from './privacyPolicy/PrivacyPolicy';
import DeactivateAccount from './deactivateAccount/DeactivateAccount';
import DeactivateAccountSuccess from './deactivateAccountSuccess/DeactivateAccountSuccess';
import { genFallbackRoute, getQueryParam } from '../../shared/Navigation';

interface IRouteProps {
  userObject: ICognitoUser;
}
class Routes extends Component<IRouteProps> {
  public render() {
    return (
      <Switch>
        <Route
          key="LoginRoute"
          exact={true}
          path={RouteNames.Login}
          component={Login}
        />
        <Route
          key="CreateAccountRoute"
          exact={true}
          path={RouteNames.CreateAccount}
          component={CreateAccount}
        />
        <Route
          key="CreateAccountSuccess"
          exact={true}
          path={RouteNames.CreateAccountSuccess}
          component={CreateAccountSuccess}
        />
        <Route
          key="AccountVerification"
          exact={true}
          path={RouteNames.AccountVerification}
          component={AccountVerification}
        />
        <ProtectedRoute
          key="UserPreferencesRoute"
          exact={true}
          path={RouteNames.UserPreferences}
          component={Preferences}
          predicate={Rules.IsSignedInAndAcceptedTOS(this.props.userObject)}
          fallback={genFallbackRoute(RouteNames.Home)}
        />
        <Route
          key="ForgotPassword"
          exact={true}
          path={RouteNames.ForgotPassword}
          component={ForgotPassword}
        />
        <Route
          key="ForgotPasswordSuccess"
          exact={true}
          path={RouteNames.ForgotPasswordSuccess}
          component={ForgotPasswordSuccess}
        />
        <Route
          key="ForgotPasswordCode"
          exact={true}
          path={RouteNames.ForgotPasswordCode}
          component={ForgotPasswordCode}
        />
        <ProtectedRoute
          key="DeactivateAccountRoute"
          exact={true}
          path={RouteNames.DeactivateAccount}
          component={DeactivateAccount}
          predicate={Rules.IsSignedInAndAcceptedTOS(this.props.userObject)}
          fallback={genFallbackRoute(RouteNames.Home)}
        />
        <Route
          key="DeactivateAccountSuccess"
          exact={true}
          path={RouteNames.DeactivateAccountSuccess}
          component={DeactivateAccountSuccess}
        />
        <ProtectedRoute
          key="ManageHabitatsRoute"
          exact={true}
          path={RouteNames.ManageHabitats}
          component={ManageHabitats}
          predicate={
            Rules.IsSignedInAndAcceptedTOS(this.props.userObject) &&
            Rules.IsAdminUser(this.props.userObject)
          }
          fallback={genFallbackRoute(RouteNames.Home)}
        />
        <ProtectedRoute
          key="PreferencesRoute"
          exact={true}
          path={RouteNames.UserPreferences}
          component={Preferences}
          predicate={Rules.IsSignedInAndAcceptedTOS(this.props?.userObject)}
          fallback={genFallbackRoute(RouteNames.Home)}
        />
        <ProtectedRoute
          key="ProfileRoute"
          exact={true}
          path={RouteNames.MyProfile}
          component={Profile}
          predicate={Rules.IsSignedInAndAcceptedTOS(this.props?.userObject)}
          fallback={genFallbackRoute(RouteNames.Home)}
        />
        <ProtectedRoute
          key="HelpAndInfoRoute"
          exact={true}
          path={RouteNames.Info}
          component={HelpAndInfo}
          predicate={Rules.IsSignedInAndAcceptedTOS(this.props?.userObject)}
          fallback={genFallbackRoute(RouteNames.Home)}
        />
        <Route
          key="Home"
          exact={true}
          path={RouteNames.Home}
          render={() => {
            let route;
            const userToken = RouteRules.IsUserLoggedIn(this.props?.userObject);
            const userTosAccepted = Rules.IsSignedInAndAcceptedTOS(
              this.props?.userObject
            );
            const userPreferencesSaved = Rules.IsSignedInAndSavedPreferences(
              this.props?.userObject
            );

            if (!userToken) {
              const returnUrl = getQueryParam('returnUrl');

              const routeUrl = returnUrl
                ? `${RouteNames.Login}?returnUrl=${returnUrl}`
                : RouteNames.Login;

              route = <Redirect to={routeUrl} />;
            } else if (!userTosAccepted) {
              route = <Redirect to={RouteNames.TermsOfService} />;
            } else if (!userPreferencesSaved) {
              route = <Redirect to={RouteNames.UserPreferences} />;
            } else {
              route = <Redirect to={RouteNames.Observations} />;
            }

            return route;
          }}
        />
        <ProtectedRoute
          key="TermsOfServiceRoute"
          exact={true}
          path={RouteNames.TermsOfService}
          component={TermsOfService}
          predicate={Rules.IsUserLoggedIn(this.props?.userObject)}
          fallback={genFallbackRoute(RouteNames.Home)}
        />
        <ProtectedRoute
          key="Info"
          exact={true}
          path={RouteNames.Info}
          component={HelpAndInfo}
          predicate={Rules.IsSignedInAndAcceptedTOS(this.props?.userObject)}
          fallback={genFallbackRoute(RouteNames.Home)}
        />
        <ProtectedRoute
          key="Observations"
          exact={true}
          path={RouteNames.Observations}
          component={SavedObservations}
          predicate={Rules.IsSignedInAndAcceptedTOS(this.props?.userObject)}
          fallback={genFallbackRoute(RouteNames.Home)}
        />
        <ProtectedRoute
          key="ManageUsers"
          exact={true}
          path={RouteNames.ManageUsers}
          component={ManageUsers}
          predicate={Rules.IsSignedInAndAcceptedTOS(this.props?.userObject)}
          fallback={genFallbackRoute(RouteNames.Home)}
        />
        <ProtectedRoute
          key="EditObservations"
          exact={true}
          path={RouteNames.EditObservations}
          component={EditObservations}
          predicate={Rules.IsSignedInAndAcceptedTOS(this.props?.userObject)}
          fallback={genFallbackRoute(RouteNames.Home)}
        />
        <ProtectedRoute
          key="CreateObservations"
          exact={true}
          path={RouteNames.CreateObservations}
          component={CreateObservations}
          predicate={Rules.IsSignedInAndAcceptedTOS(this.props?.userObject)}
          fallback={genFallbackRoute(RouteNames.Home)}
        />
        <Route
          key="PrivacyPolicy"
          exact={true}
          path={RouteNames.PrivacyPolicy}
          component={PrivacyPolicy}
        />
        <Route
          key="RouteNotFound-404"
          render={() => <div>404 Page not found</div>}
        />
      </Switch>
    );
  }
}

const stateToProps = (state: ReduxStore) => {
  return { userObject: state.User.UserObject };
};

export default connect(stateToProps)(Routes);
